// src/accounts/pages/AccountDetailPage.jsx
//
// Account-scoped role grants for one Account. Route: /admin/accounts/:accountId.
// Rebuilt to match admin/pages/UsersPage.jsx's shared/ui-based style —
// PageHeader with breadcrumbs, a StatTile summary row, one DataTable,
// grant/edit behind AccountRoleGrantModal, ConfirmModal for removal. Moved
// from pages/AccountDetailPage.jsx, replacing a hand-rolled <table>, an
// always-open inline form, and styles/AccountsPage.css.
//
// The route is gated on account:manage at the router level (see
// AppRoutes.jsx), so reaching here implies holding it — no internal
// re-check, same reasoning UsersPage.jsx gives for admin:manage_users.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Plus, Users, KeyRound, Layers } from "lucide-react";
import {
  PageHeader,
  Button,
  EmptyState,
  DataTable,
  StatTile,
  Alert,
  ConfirmModal,
  SkeletonCardGrid,
  SkeletonTable,
} from "../../shared/ui";
import { accountApi } from "../../api/accountApi";
import { listRoles } from "../../admin/api/rolesApi";
import { toAdminError } from "../../admin/api/adminApiError";
import AccountRoleGrantModal from "../components/AccountRoleGrantModal";

export default function AccountDetailPage() {
  const { accountId } = useParams();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState(null);

  // null = closed. { userId: null } = grant, a binding = edit.
  const [formTarget, setFormTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await accountApi.get(accountId);
      setAccount(data);
    } catch (err) {
      setLoadError(toAdminError(err, "Failed to load the account.").message);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      // Only account-level roles are grantable here — the backend itself
      // rejects any other level (see GrantAccountRoleHandler); filtering
      // server-side means the picker never even offers a choice that would
      // always fail.
      setRoles(await listRoles({ level: "account" }));
    } catch (err) {
      setRolesError(err?.message || "Failed to load assignable roles.");
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const roleNameById = useCallback((id) => roles.find((r) => r.id === id)?.name, [roles]);

  // account.role_bindings is one row per (user_id, role_id) grant — grouped
  // here into one entry per user, the same "one row per person, roles as a
  // set" shape UsersPage's own user list already presents.
  const bindings = useMemo(() => {
    const byUser = new Map();
    for (const b of account?.role_bindings || []) {
      if (!byUser.has(b.user_id)) {
        byUser.set(b.user_id, { userId: b.user_id, username: b.username, email: b.email, roleIds: [] });
      }
      byUser.get(b.user_id).roleIds.push(b.role_id);
    }
    return [...byUser.values()];
  }, [account]);

  // Replaces this user's whole role_bindings set on this account in one
  // call — the batch counterpart of grantRole/revokeRole, the same way
  // UsersPage's one role_ids field replaces a user's whole org-wide role
  // set instead of adding to it. Works identically for a brand-new grant
  // and an edit — no roster step to precede it either way (see
  // AccountAggregate's own docstring).
  const handleSubmit = useCallback(
    async ({ userId, roleIds }) => {
      const roleNames = roleIds.map(roleNameById).filter(Boolean);
      try {
        const { data } = await accountApi.setRoles(accountId, userId, roleNames);
        setAccount(data);
        const binding = (data.role_bindings || []).find((b) => b.user_id === userId);
        return { username: binding?.username || formTarget?.username || `User #${userId}` };
      } catch (err) {
        throw toAdminError(err, "Save failed. Please try again.");
      }
    },
    [accountId, roleNameById, formTarget]
  );

  const handleRemove = useCallback(async () => {
    const { data } = await accountApi.setRoles(accountId, removeTarget.userId, []);
    setAccount(data);
    setRemoveTarget(null);
  }, [accountId, removeTarget]);

  const unusedRoleCount = roles.filter((r) => !bindings.some((b) => b.roleIds.includes(r.id))).length;

  const columns = useMemo(
    () => [
      {
        accessorKey: "username",
        header: "User",
        cell: ({ row }) => (
          <div className="min-w-0">
            <span className="block font-medium text-ink">{row.original.username}</span>
            <span className="block text-xs text-muted mt-0.5">{row.original.email}</span>
          </div>
        ),
      },
      {
        id: "roleCount",
        header: "Roles",
        accessorFn: (row) => row.roleIds.length,
        cell: ({ row }) => {
          const ids = row.original.roleIds;
          if (ids.length === 0) {
            return <span className="text-xs text-amber-700 font-medium">No role on this account</span>;
          }
          return (
            <div className="flex items-center gap-1.5 flex-wrap">
              {ids.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-slate-700 text-[11px] font-semibold"
                >
                  {roleNameById(id) || `#${id}`}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "",
        align: "right",
        width: 180,
        sortAllowed: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setFormTarget(row.original)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setRemoveTarget(row.original)}
            >
              Remove access
            </Button>
          </div>
        ),
      },
    ],
    [roleNameById]
  );

  if (loading && !account) {
    return (
      <div>
        <SkeletonCardGrid cards={3} className="mb-5" />
        <SkeletonTable rows={5} columns={3} className="rounded-card border border-line bg-white" />
      </div>
    );
  }

  if (!account) {
    return (
      <div>
        <PageHeader title="Account" breadcrumbs={[{ label: "Accounts", href: "/admin/accounts" }]} />
        <EmptyState
          icon={ShieldCheck}
          title="Couldn't load account"
          description={loadError || "Account not found."}
          action={
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={account.name}
        subtitle="Account-scoped role grants — effective immediately, on every Project under this Account."
        breadcrumbs={[{ label: "Accounts", href: "/admin/accounts" }, { label: account.name }]}
        actions={
          <Button variant="primary" onClick={() => setFormTarget({ userId: null })}>
            <Plus size={15} /> Grant role
          </Button>
        }
      />

      {bindings.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <StatTile icon={Users} label="People With Access" value={bindings.length} />
          <StatTile icon={KeyRound} label="Role Grants" value={account.role_bindings?.length || 0} />
          <StatTile
            icon={Layers}
            label="Unused Roles"
            value={unusedRoleCount}
            hint={roles.length ? `of ${roles.length} in the catalog` : undefined}
          />
        </div>
      )}

      {rolesError && (
        <Alert variant="danger" title="Assignable roles unavailable" className="mb-4">
          {rolesError} Grants are listed below, but creating or editing one is disabled until
          this loads.{" "}
          <button type="button" onClick={loadRoles} className="underline font-semibold">
            Retry
          </button>
        </Alert>
      )}

      {bindings.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No role grants yet"
          description="Grant an account-scoped role to give someone access to this Account and everything under it."
          action={
            <Button variant="secondary" onClick={() => setFormTarget({ userId: null })}>
              <Plus size={15} /> Grant role
            </Button>
          }
        />
      ) : (
        <DataTable table={{ columns, rows: bindings }} canSearch />
      )}

      {/* Keyed so switching between grant and editing a different row
          re-seeds the form's initial state instead of keeping stale input. */}
      <AccountRoleGrantModal
        key={formTarget?.userId ?? "new"}
        open={Boolean(formTarget)}
        binding={formTarget?.userId ? formTarget : null}
        roles={roles}
        rolesLoading={rolesLoading}
        rolesError={rolesError}
        onClose={() => setFormTarget(null)}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        open={Boolean(removeTarget)}
        entityLabel="access grant"
        name={removeTarget?.username}
        confirmLabel="Remove"
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
      >
        {removeTarget?.username} loses every role on this Account. This can be undone by
        granting a role again.
      </ConfirmModal>
    </div>
  );
}
